import {
  AccommodationCatalogConsoleListingRedirectMechanismsFragment,
  AccommodationConsoleListingPricingFragment,
  AccommodationConsoleListingPropertyFragment,
  AccommodationPlaceType,
  useAccommodationPlacesByCoupleQuery,
  useAccommodationsConsolePropertiesQuery,
} from '@graphql/generated';
import { useEventInfo } from '@shared/utils/eventInfo';
import { addAction, addError } from '@shared/utils/logger';
import { useMemo } from 'react';
import {
  AccommodationListingDTO,
  AccommodationListingDTOBase,
  AccommodationPlace,
  AccommodationsConsoleState,
  RoomBlockAccommodationListingDTO,
  RoomBlockWithPricePerNight,
} from '../AccommodationsConsole.types';
import { UseHotelRoomBlockPriceMappingReturn, useHotelRoomBlockPriceMapping, useStandardHotelRoomPriceMapping } from './useListingPriceMapping';
import { ApolloError } from '@apollo/client';
import { isValidLatLngLocation } from '@shared/utils/locationValidation';
import { useAccommodationsConsoleConfig } from '../AccommodationsConsoleConfigContext';
import { useApiMetadata } from '@shared/hooks/useApiMetadata';
import { extractHotelRedirects } from '@shared/utils/extractHotelRedirects';
import { useAccommodationPlacesPricing } from './useAccommodationPlacesPricing';

type SupportedJoyHotelAccommodation = Merge<
  AccommodationPlace,
  {
    name: string;
    lat: number;
    long: number;
    type: AccommodationPlaceType.hotel;
    isFromAccommodationCatalog: true;
    accommodationRoomBlock: RoomBlockWithPricePerNight | null;
  }
>;
type SupportedHotelRoomBlockAccommodation = Merge<
  AccommodationPlace,
  {
    name: string;
    lat: number;
    long: number;
    type: AccommodationPlaceType.custom;
    accommodationRoomBlock: RoomBlockWithPricePerNight;
  }
>;

type SupportEventAccommodationPlace = SupportedJoyHotelAccommodation | SupportedHotelRoomBlockAccommodation;

const isSupportedAccommodation = (place: AccommodationPlace, shouldFilterOutNonJoyRoomBlocks: boolean): place is SupportEventAccommodationPlace => {
  return (
    // Is named
    !!(place.name || place.customName) &&
    // Is one of the supported types:
    //  - A Joy hotel -- hotel or partner room block
    //  - A custom room block
    ((place.type === 'hotel' && place.isFromAccommodationCatalog) || (!shouldFilterOutNonJoyRoomBlocks && place.type === 'custom' && !!place.accommodationRoomBlock)) &&
    // If is room block, does it have a nightly rate
    (place.accommodationRoomBlock ? typeof place.accommodationRoomBlock.pricePerNight === 'number' : true) &&
    // Has coordinates (needs to be pinnable to the map)
    isValidLatLngLocation({ latitude: place.lat, longitude: place.long })
  );
};

type UseAccommodationsConsoleListingsArgs = {
  state: AccommodationsConsoleState;
};

const deriveRoomBlockListingInfo = (
  baseInfo: AccommodationListingDTOBase,
  place: Omit<SupportedHotelRoomBlockAccommodation, 'type'>,
  mapHotelRoomBlockPrice: UseHotelRoomBlockPriceMappingReturn,
): RoomBlockAccommodationListingDTO => {
  const maybePhotoUrl = (place.photo || place.customPhoto)?.url;
  const roomBlock = place.accommodationRoomBlock;
  // Need to handle the scenario of roomblock cutoff date
  return {
    ...baseInfo,
    type: 'roomBlock',
    bookingUrl: roomBlock.bookingUrl,
    cutoffDate: roomBlock.cutoffDate,
    interestedGuestCount: roomBlock.interestedGuestCount,
    numberOfRoomsReserved: roomBlock.numberOfRoomsReserved,
    mediaAssets: {
      isLoading: false,
      photos:
        // If there are photos in the base info, those photos are provided by the joy accommodation.
        // We prefer those.
        baseInfo.mediaAssets.photos.length
          ? baseInfo.mediaAssets.photos
          : maybePhotoUrl
            ? [
                {
                  url: maybePhotoUrl,
                },
              ]
            : [],
    },
    pricingInfo: {
      isLoading: false,
      values: mapHotelRoomBlockPrice(roomBlock),
    },
  };
};

type DeriveBaseAccommodationListingInfoFromEventAccommodationArgs = {
  place: SupportEventAccommodationPlace;
  extendedAccommodationData: AccommodationConsoleListingPropertyFragment | null;
  isLoadingExtendedAccommodationData: boolean;
  errorLoadingExtendedAccommodationsData: Maybe<ApolloError>;
};
const deriveBaseAccommodationListingInfoFromEventAccommodation = ({
  place,
  extendedAccommodationData,
  isLoadingExtendedAccommodationData,
  errorLoadingExtendedAccommodationsData,
}: DeriveBaseAccommodationListingInfoFromEventAccommodationArgs): AccommodationListingDTOBase => {
  return {
    isPickedByCouple: true,

    // The following fields are directly available on the event accommodation
    id: place.id,
    joyPlaceId: place.joyPlaceId,
    name: place.customName || place.name,
    isFavorite: place.isFavorite,
    userNote: place.userNote,
    location: {
      lat: place.lat,
      lng: place.long,
    },
    address: {
      street: place.address,
      city: place.city,
      country: place.country,
    },
    starRating: place.starRating,

    // Need to use the `searchAccommodationProperties` API data for the following fields:
    mediaAssets: extendedAccommodationData
      ? {
          isLoading: extendedAccommodationData.photos ? false : isLoadingExtendedAccommodationData,
          photos: extendedAccommodationData.photos,
        }
      : {
          isLoading: !errorLoadingExtendedAccommodationsData,
          photos: (place.photo?.url && [{ url: place.photo.url }]) || [],
        },
    userRating: extendedAccommodationData?.userRating
      ? {
          ratingValue: extendedAccommodationData.userRating.ratingValue,
          ratingCount: extendedAccommodationData.userRating.ratingCount,
        }
      : null,
    facilities: extendedAccommodationData?.facilities || [],
  };
};

export const useAccommodationPlacesByCouple = (args: UseAccommodationsConsoleListingsArgs) => {
  const { state } = args;
  const { eventHandle } = useEventInfo();
  const config = useAccommodationsConsoleConfig();

  const { deviceType, anonymousId, eventId, userCountryCode } = useApiMetadata();

  const { data, loading } = useAccommodationPlacesByCoupleQuery({
    variables: {
      name: eventHandle || '',
    },
    batchMode: 'fast',
    onCompleted(data) {
      if (!data?.eventByName?.accommodationPlacesV2?.length) {
        addAction('Accommodation places by couple is empty (AccommodationsConsole)', {
          context: 'accommodationPlaces',
          eventHandle,
        });
      }
    },
    onError: error => {
      addError(error, {
        source: 'Accommodation places by couple api failed (AccommodationsConsole)',
        context: 'accommodationPlaces',
        errorMessage: error.message,
        eventHandle,
      });
    },
  });

  const accommodationPlaceIds = data?.eventByName?.accommodationPlacesV2?.map(x => x.id) || [];
  const accommodationPlaceIdsToFetchPrices = data?.eventByName?.accommodationPlacesV2?.filter(accom => accom.type === 'hotel').map(x => x.id) || [];

  // Load pricing data
  const {
    data: pricingData,
    loading: isFetchingListingPrices,
    error: errorFetchingAccommodationPrices,
  } = useAccommodationPlacesPricing(accommodationPlaceIdsToFetchPrices, {
    dates: {
      // Fetch event accommodation prices based on **state** search dates
      checkIn: state.searchDates.checkin,
      checkOut: state.searchDates.checkout,
    },
    occupancies: state.occupancies,
  });

  // Get extended joy hotel data
  // Progressively layer in this data as it loads
  const {
    data: extendedAccommodationData,
    loading: isLoadingExtendedAccommodationData,
    error: errorLoadingExtendedAccommodationsData,
  } = useAccommodationsConsolePropertiesQuery({
    variables: {
      payload: {
        propertyIdentifiers: accommodationPlaceIds.map(x => ({ accommodationPlaceId: x })),
        metadata: {
          eventId,
          deviceType,
          anonymousId,
          userCountryCode,
        },
      },
    },
    onError: error =>
      addError(error, {
        source: 'Search accommodations properties with propertyIdentifiers api failed (AccommodationsConsole)',
        context: 'searchAccommodationProperties',
        errorMessage: error.message,
        propertyIdentifiers: accommodationPlaceIds,
      }),
    onCompleted(data) {
      if (!data?.searchAccommodationProperties?.properties?.length) {
        addAction('Accommodation properties is empty (AccommodationsConsole)', {
          context: 'searchAccommodationProperties',
          propertyIdentifiers: accommodationPlaceIds,
        });
      }
    },
    batchMode: 'off',
  });

  const mapStandardHotelRoomPrice = useStandardHotelRoomPriceMapping();
  const mapHotelRoomBlockPrice = useHotelRoomBlockPriceMapping();

  const filteredAccommodationPlaces = useMemo(() => {
    const supportedAccommodations =
      data?.eventByName?.accommodationPlacesV2?.filter((place): place is SupportEventAccommodationPlace =>
        isSupportedAccommodation(place, config.listingsPanel.listingData.shouldFilterOutNonJoyRoomBlocks),
      ) || [];

    // Collect pricing information
    const pricingByAccommodationPlaceId =
      pricingData?.travelPlacesPricingAndBookingDetails?.accommodationPlaces?.reduce(
        (acc, place) => {
          acc[place.accommodationPlace.id] = place.pricingInfo;
          return acc;
        },
        {} as { [accommodationId: string]: Maybe<AccommodationConsoleListingPricingFragment> },
      ) || {};

    const redirectMechanismsByAccommodationPlaceId =
      pricingData?.travelPlacesPricingAndBookingDetails?.accommodationPlaces?.reduce(
        (acc, place) => {
          acc[place.accommodationPlace.id] = place.redirectMechanisms;
          return acc;
        },
        {} as { [accommodationId: string]: Maybe<AccommodationCatalogConsoleListingRedirectMechanismsFragment[]> },
      ) || {};

    const extendedAccommodationDataByJoyPlaceId =
      extendedAccommodationData?.searchAccommodationProperties?.properties.reduce(
        (acc, property) => {
          acc[property.joyPlaceId] = property;
          return acc;
        },
        {} as { [joyPlaceId: string]: AccommodationConsoleListingPropertyFragment },
      ) || {};

    return supportedAccommodations.map<AccommodationListingDTO>(place => {
      const extendedAccommodationData = place.joyPlaceId ? extendedAccommodationDataByJoyPlaceId[place.joyPlaceId] : null;

      const baseInfo = deriveBaseAccommodationListingInfoFromEventAccommodation({
        place,
        extendedAccommodationData,
        isLoadingExtendedAccommodationData,
        errorLoadingExtendedAccommodationsData,
      });

      // Custom room block
      if (place.type === AccommodationPlaceType.custom) {
        return deriveRoomBlockListingInfo(baseInfo, place, mapHotelRoomBlockPrice);
      }
      // Partner hotel room block
      else if (place.accommodationRoomBlock) {
        return deriveRoomBlockListingInfo(
          baseInfo,
          // Need to spread the object so that TypeScript recognizes the type correctly
          { ...place, accommodationRoomBlock: place.accommodationRoomBlock },
          mapHotelRoomBlockPrice,
        );
      } else {
        // Handle the joy hotel
        const pricingInfo = pricingByAccommodationPlaceId[place.id];
        return {
          ...baseInfo,
          type: 'standardHotel',
          isSoldOutForSelectedDates: !!extractHotelRedirects(redirectMechanismsByAccommodationPlaceId[place.id])?.hotelSearchPageUrl,
          redirectMechanisms: redirectMechanismsByAccommodationPlaceId[place.id] || [],
          pricingInfo: pricingInfo
            ? {
                isLoading: false,
                values: mapStandardHotelRoomPrice(pricingInfo),
              }
            : {
                isLoading: isFetchingListingPrices,
                values: null,
              },
        };
      }
    });
  }, [
    extendedAccommodationData,
    isLoadingExtendedAccommodationData,
    errorLoadingExtendedAccommodationsData,
    data?.eventByName?.accommodationPlacesV2,
    pricingData,
    isFetchingListingPrices,
    mapStandardHotelRoomPrice,
    mapHotelRoomBlockPrice,
    config.listingsPanel.listingData.shouldFilterOutNonJoyRoomBlocks,
  ]);

  return {
    eventAccommodations: filteredAccommodationPlaces,
    isFetchingEventAccommodations: loading,
    errorFetchingAccommodationPrices,
  };
};
